import React, { useState, useEffect } from 'react';
import { registrationAPI } from '../../services/api';
import './MyCoursesPage.css';

interface Course {
  id: number;
  title: string;
  category: string;
  year: string;
  grade: string;
  difficulty: string;
  difficultyLevel: number;
  targetGrade: string;
  schedule: string;
  location: string;
  totalLessons: number;
  status: string;
  teacher: {
    name: string;
    avatar?: string;
  };
}

const MyCoursesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('0');
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  const tabs = [
    { key: '0', label: '全部' },
    { key: '1', label: '正常' },
    { key: '2', label: '过期' },
    { key: '3', label: '退课' },
    { key: '4', label: '转出' }
  ];

  useEffect(() => {
    fetchCourses();
  }, [activeTab]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const registrations = await registrationAPI.getMyRegistrations();
      
      // Map API data to match the Course interface
      const mappedCourses = registrations.map(reg => ({
        id: reg.courseId,
        title: reg.courseName,
        category: reg.category || '未知',
        year: reg.year || '2024年',
        grade: reg.grade || '未知',
        difficulty: '未知',
        difficultyLevel: Number(reg.course?.difficultyLevel) || 1,
        targetGrade: reg.grade || '未知',
        schedule: reg.startDate ? new Date(reg.startDate).toLocaleDateString() : '时间待定',
        location: reg.location || '地点待定',
        totalLessons: reg.totalLessons || 1,
        status: mapStatusToChinese(reg.status),
        teacher: {
          name: reg.teacherName || '未知老师',
          avatar: reg.imageUrl
        }
      }));
      
      setCourses(mappedCourses);
    } catch (error) {
      console.error('获取课程失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 状态映射函数：英文状态值 -> 中文状态值
const mapStatusToChinese = (englishStatus: string) => {
  const statusMap: { [key: string]: string } = {
    'paid': '已付款',
    'refunded': '已退款',
    'transferred': '转出',
    'active': '正常',
    'expired': '过期',
    'withdrawn': '退课'
  };
  return statusMap[englishStatus] || englishStatus;
};

const getStatusClass = (status: string) => {
  const classMap: { [key: string]: string } = {
    '正常': 'status-normal',
    '过期': 'status-expired',
    '退课': 'status-cancelled',
    '转出': 'status-transferred',
    '已付款': 'status-confirmed',
    '已退款': 'status-refunded'
  };
  return classMap[status] || '';
};

  const renderStars = (level: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <i
        key={index}
        className={`star ${index < level ? 'active' : ''}`}
      />
    ));
  };

  const filteredCourses = courses.filter(course => {
    if (activeTab === '0') return true;
    const statusMap: { [key: string]: string } = {
      '1': '正常',
      '2': '过期',
      '3': '退课',
      '4': '转出'
    };
    return course.status === statusMap[activeTab];
  });

  if (loading) {
    return (
      <div className="my-courses-page">
        <div className="courses-header">
          <h1>我的课程</h1>
        </div>
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-courses-page">
      <div className="courses-header">
        <div className="header-content">
          <h1>我的课程</h1>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs-wrapper">
          <ul className="tabs-list">
            {tabs.map(tab => (
              <li
                key={tab.key}
                className={`tab-item ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span>{tab.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="courses-content">
        {filteredCourses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-box">
              <div className="nodata">
                <img 
                  src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSJ3aGl0ZSIvPgo8Y2lyY2xlIGN4PSI2MCIgY3k9IjQwIiByPSIyNCIgZmlsbD0iI0Y1RjVGNSIvPgo8Y2lyY2xlIGN4PSI2MCIgY3k9IjQwIiByPSIxMiIgZmlsbD0iI0U2RTZFNiIvPgo8cGF0aCBkPSJNMzAgOTBDMzAgODMuMzcyOCAzNC4zNzI4IDc5IDQxIDc5SDc5Qzg1LjYyNzIgNzkgOTAgODMuMzcyOCA5MCA5MFY5NUgzMFY5MFoiIGZpbGw9IiNGNUY1RjUiLz4KPHBhdGggZD0iTTQyIDU1QzQyIDUxLjY4NjMgNDQuNjg2MyA0OSA0OCA0OUM1MS4zMTM3IDQ5IDU0IDUxLjY4NjMgNTQgNTVDNTQgNTguMzEzNyA1MS4zMTM3IDYxIDQ4IDYxQzQ0LjY4NjMgNjEgNDIgNTguMzEzNyA0MiA1NVoiIGZpbGw9IiNFOUU5RTkiLz4KPHBhdGggZD0iTTY2IDU1QzY2IDUxLjY4NjMgNjguNjg2MyA0OSA3MiA0OUM3NS4zMTM3IDQ5IDc4IDUxLjY4NjMgNzggNTVDNzggNTguMzEzNyA3NS4zMTM3IDYxIDcyIDYxQzY4LjY4NjMgNjEgNjYgNTguMzEzNyA2NiA1NVoiIGZpbGw9IiNFOUU5RTkiLz4KPC9zdmc+"
                  alt="Empty"
                  className="empty-image"
                />
                <p>暂无课程信息</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="courses-list">
            {filteredCourses.map(course => (
              <div key={course.id} className="course-card">
                <div className="course-header">
                  <h3 className="course-title">
                    <em className="category">【{course.category}】</em>
                    <em className="year">{course.year}</em>
                    {course.title}
                  </h3>
                  <div className="course-difficulty">
                    <span className="difficulty-name">{course.difficulty}</span>
                    <div className="difficulty-stars">
                      {renderStars(course.difficultyLevel)}
                    </div>
                  </div>
                </div>

                <div className="course-details">
                  <div className="detail-item">
                    <span className="label">适合年级：</span>
                    <span className="content">{course.targetGrade}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">上课时间：</span>
                    <span className="content">{course.schedule}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">上课地址：</span>
                    <span className="content">
                      <em>{course.location.split('(')[0].trim()}</em>
                      <em>({course.location.split('(')[1]?.replace(')', '')})</em>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">课节总数：</span>
                    <span className="content">{course.totalLessons}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">状态：</span>
                    <span className={`content ${getStatusClass(course.status)}`}>
                      {course.status}
                    </span>
                  </div>
                  <div className="detail-item teacher-info">
                    <span className="label">授课老师：</span>
                    <span className="content">
                      <em>{course.teacher.name}</em>
                    </span>
                    {course.teacher.avatar && (
                      <img 
                        src={course.teacher.avatar} 
                        alt={course.teacher.name}
                        className="teacher-avatar"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div className="list-finished-text">没有更多了</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCoursesPage;